import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Form, Spinner } from 'react-bootstrap-5';
import CallForm, { CALL_RESULTS } from './call_form';
import { createActivity, evaluateTemplate } from '../../../helpers/connect_api';
import OtherForm from './other_form';
import MeetingForm from './meeting_form';
import {
  ACTIVITY_TYPE_CALL, ACTIVITY_TYPE_EMAIL, ACTIVITY_TYPE_MEETING, ACTIVITY_TYPE_OTHER, CONTACT_MODEL, PROPERTY_MODEL
} from '../../../constants';
import ContactInput from './contact_input';
import { addActivity } from '../store/actions/activities';
import { connect } from 'react-redux';
import ButtonGroupRadio from './button_group_radio';
import { faCalendar, faFloppyDisk, faPencil, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import EmailForm from './email_form';

const TYPE_OPTIONS = [
  { icon: faPhone, label: 'Call', value: ACTIVITY_TYPE_CALL, },
  { icon: faEnvelope, label: 'Email', value: ACTIVITY_TYPE_EMAIL },
  { icon: faCalendar, label: 'Meeting', value: ACTIVITY_TYPE_MEETING },
  { icon: faPencil, label: 'Other', value: ACTIVITY_TYPE_OTHER }
];

const DEFAULT_NEW_ACTIVITY = {
  callResult: CALL_RESULTS[0].value,
  description: '',
  location: '',
  emailSubject: '',
  type: ACTIVITY_TYPE_CALL,
  connectedRecordsAttributes: []
};

function NewActivityForm({ addActivity, callListId, callTemplates, recordId, recordType }) {
  const [activity, setActivity] = useState(DEFAULT_NEW_ACTIVITY);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState();

  useEffect(() => {
    if (!selectedTemplate) {
      setActivity(prevActivity => ({ ...prevActivity, script: undefined }));
      return;
    }

    const propertyId = recordType === PROPERTY_MODEL ? recordId : undefined;
    const contactId = activity.connectedRecordsAttributes
      .find(record => !record._destroy && record.connectedRecordType === CONTACT_MODEL)?.connectedRecordId;

    evaluateTemplate(selectedTemplate, contactId, propertyId)
      .then(script => setActivity(prevActivity => ({ ...prevActivity, script })));
  }, [selectedTemplate, activity.connectedRecordsAttributes]);

  const handleSave = () => {
    setSaving(true);

    const activityParams = {
      ...activity,
      connectedRecordsAttributes: [
        ...activity.connectedRecordsAttributes,
        { connectedRecordId: recordId, connectedRecordType: recordType }
      ]
    };

    createActivity(activityParams, callListId)
      .then((activity) => {
        addActivity(activity);
        setSelectedTemplate();
        setActivity(DEFAULT_NEW_ACTIVITY);
      })
      .catch(() => {
        // errors
      })
      .finally(() => setSaving(false));
  };

  let form;
  if (activity.type === ACTIVITY_TYPE_CALL) {
    form = (
      <CallForm
        activity={activity}
        callTemplates={callTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateSelect={setSelectedTemplate}
        onUpdate={setActivity}
      />
    );
  }
  if (activity.type === ACTIVITY_TYPE_EMAIL) form = <EmailForm activity={activity} onUpdate={setActivity} />;
  if (activity.type === ACTIVITY_TYPE_OTHER) form = <OtherForm activity={activity} onUpdate={setActivity} />;
  if (activity.type === ACTIVITY_TYPE_MEETING) form = <MeetingForm activity={activity} onUpdate={setActivity} />;

  return (
    <React.Fragment>
      <Card.Body className="d-flex flex-column gap-3">
        <Form.Group>
          <Form.Label>Activity Type</Form.Label>
          <ButtonGroupRadio
            checked={activity.type}
            name="activity_type"
            options={TYPE_OPTIONS}
            onChange={type => setActivity({ ...activity, type })}
          />
        </Form.Group>
        <ContactInput activity={activity} onChange={setActivity} />
        {form}
      </Card.Body>
      <Card.Footer className="text-end">
        <Button disabled={saving} variant="secondary" onClick={handleSave}>
          {saving && <Spinner animation="grow" />}
          {!saving && <FontAwesomeIcon icon={faFloppyDisk} />}
          Save Activity
        </Button>
      </Card.Footer>
    </React.Fragment>
  );
}

NewActivityForm.propTypes = {
  addActivity: PropTypes.func.isRequired,
  callListId: PropTypes.number.isRequired,
  callTemplates: PropTypes.array.isRequired,
  recordId: PropTypes.number.isRequired,
  recordType: PropTypes.string.isRequired
};

export default connect(undefined, { addActivity })(NewActivityForm);
